import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import C from '../../data/colors';
import { theme } from '../../data/colors';
import { adminAuthHeader } from '../../data/api';
import AdminPageHeader from './AdminPageHeader';
import { Shield, Download, Upload, RefreshCw, CheckCircle, AlertTriangle, Clock, Database, FileText, Archive, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DataProtectionTab = () => {
  const [status, setStatus] = useState(null);
  const [backups, setBackups] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [restoreModal, setRestoreModal] = useState(null);
  const [restoreState, setRestoreState] = useState({ confirmation: '', dryRun: false, collections: null, running: false, result: null });
  const [error, setError] = useState(null);

  const getAuthHeaders = () => ({
    headers: adminAuthHeader()
  });

  const loadStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/data-protection/status`, getAuthHeaders());
      setStatus(r.data);
    } catch (e) {
      console.error('Failed to load status:', e);
    }
  }, []);

  const loadBackups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/backups`, getAuthHeaders());
      setBackups(r.data);
    } catch (e) {
      console.error('Failed to load backups:', e);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/backup/status`, getAuthHeaders());
      setHealth(r.data);
    } catch (e) {
      console.error('Failed to load backup health:', e);
    }
  }, []);

  const fmtWhen = (iso) => {
    if (!iso) return 'Never';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return String(iso);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadStatus(), loadBackups(), loadHealth()]);
      setLoading(false);
    };
    load();
  }, [loadStatus, loadBackups, loadHealth]);

  const createBackup = async () => {
    setCreating(true);
    setError(null);
    try {
      await axios.post(`${API}/api/admin/backup`, {}, getAuthHeaders());
      await loadBackups();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create backup');
    }
    setCreating(false);
  };

  const downloadBackup = async (backupId) => {
    try {
      const response = await axios.get(`${API}/api/admin/backup/${backupId}/download`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${backupId}.json.gz.enc`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError('Failed to download backup');
    }
  };

  const exportData = async (type) => {
    setExporting(type);
    try {
      const endpoint = type === 'all' ? 'all' : type;
      const response = await axios.get(`${API}/api/admin/export/${endpoint}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      const ext = type === 'all' ? 'zip' : 'csv';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError(`Failed to export ${type}`);
    }
    setExporting(null);
  };

  const openRestoreModal = async (backup) => {
    try {
      const r = await axios.get(`${API}/api/admin/backup/${backup.backup_id}/validate`, getAuthHeaders());
      setRestoreModal({ ...backup, validation: r.data });
      setRestoreState({ confirmation: '', dryRun: false, collections: null, running: false, result: null });
    } catch (e) {
      setError('Failed to validate backup');
    }
  };

  const runRestore = async (dryRun = false) => {
    setRestoreState(s => ({ ...s, running: true, result: null }));
    try {
      const r = await axios.post(`${API}/api/admin/restore`, {
        backup_id: restoreModal.backup_id,
        collections: restoreState.collections,
        confirmation_phrase: restoreState.confirmation,
        dry_run: dryRun
      }, getAuthHeaders());
      setRestoreState(s => ({ ...s, running: false, result: r.data }));
      if (!dryRun && r.data.status === 'completed') {
        await loadBackups();
      }
    } catch (e) {
      setRestoreState(s => ({ ...s, running: false, result: { status: 'failed', error: e.response?.data?.detail || 'Restore failed' } }));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div data-testid="data-protection-tab">
      <AdminPageHeader eyebrow="Control" title="Data Protection" tagline="Backups, encryption, and data retention" />

      {error && (
        <div style={{ background: theme.statusErrorTint, border: `1px solid ${theme.statusError}30`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={18} color={theme.statusError} />
          <span style={{ color: theme.statusError, fontSize: 14 }}>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: theme.statusError }}>×</button>
        </div>
      )}

      {/* Status Banner */}
      <div style={{ background: C.bgCard, borderRadius: 12, padding: 20, marginBottom: 20, border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Shield size={24} color={C.gold} />
          <h2 style={{ fontSize: 18, fontWeight: 600, color: C.textPrimary, margin: 0 }}>Data Protection Status</h2>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <StatusItem 
            label="Cloud Storage (R2)" 
            status={status?.r2_configured} 
            ok="Connected"
            notOk="Not configured"
          />
          <StatusItem 
            label="Encryption" 
            status={status?.encryption_configured}
            ok="Enabled"
            notOk="Disabled"
          />
          <StatusItem 
            label="Environment" 
            value={status?.environment || 'unknown'}
          />
          <StatusItem 
            label="Retention" 
            value={`${status?.retention_days || 14} days`}
          />
        </div>

        {!status?.r2_configured && (
          <div style={{ marginTop: 16, padding: 12, background: theme.statusWarningTint, borderRadius: 8, fontSize: 13, color: theme.statusWarning }}>
            <strong>Note:</strong> R2 cloud storage not configured. Backups stored locally only. Configure R2_ACCESS_KEY_ID, R2_SECRET_ACCESS_KEY, and R2_ENDPOINT_URL in environment.
          </div>
        )}
      </div>

      {/* Backup Health — last / next / where copies live */}
      <div style={{ background: C.bgCard, borderRadius: 12, padding: 20, marginBottom: 20, border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Archive size={20} color={C.gold} />
          <h3 style={{ fontSize: 16, fontWeight: 600, color: C.textPrimary, margin: 0 }}>Backup Health</h3>
          <button onClick={loadHealth} title="Refresh" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: C.textSecondary, display: 'flex', alignItems: 'center' }}>
            <RefreshCw size={14} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 16, marginBottom: 18 }}>
          <StatusItem label="Last backup" value={fmtWhen(health?.last_backup?.created_at)} />
          <StatusItem
            label="Automatic backups"
            status={!!health?.auto_backup?.enabled}
            ok={`On · every ${health?.auto_backup?.interval_hours || 24}h`}
            notOk="Off"
          />
          <StatusItem
            label="Next scheduled"
            value={health?.auto_backup?.enabled ? fmtWhen(health?.auto_backup?.next_due_estimate) : '—'}
          />
          <StatusItem label="Backups on file" value={String(health?.backup_count ?? 0)} />
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          Where your copies live
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <StatusItem
            label="Encrypted (AES)"
            status={!!health?.encryption?.enabled}
            ok="Yes"
            notOk="Not yet — key not set"
          />
          <StatusItem
            label="Secure cloud copy"
            status={!!health?.destinations?.r2_cloud}
            ok={health?.destinations?.r2_bucket || 'Connected'}
            notOk="Not active yet"
          />
          <StatusItem
            label="Your own database"
            status={!!health?.destinations?.own_mongodb_mirror}
            ok={health?.destinations?.mirror_db ? `Mirroring → ${health.destinations.mirror_db}` : 'Connected'}
            notOk="Not active yet"
          />
        </div>

        {health && !health?.destinations?.own_mongodb_mirror && (
          <div style={{ marginTop: 14, padding: 12, background: theme.statusWarningTint, borderRadius: 8, fontSize: 13, color: theme.statusWarning }}>
            <strong>A copy into your own database isn't switched on yet.</strong> Once enabled, a readable copy of your data is kept automatically in a database you control — retrievable even if the hosting platform is unavailable.
          </div>
        )}
      </div>

      {/* Backups Section */}
      <div style={{ background: C.bgCard, borderRadius: 12, padding: 20, marginBottom: 20, border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Database size={20} color={C.textPrimary} />
            <h3 style={{ fontSize: 16, fontWeight: 600, color: C.textPrimary, margin: 0 }}>Backups</h3>
          </div>
          <button 
            data-testid="create-backup-btn"
            onClick={createBackup} 
            disabled={creating}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: C.gold, color: theme.textPrimary, border: 'none', borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 500, cursor: creating ? 'wait' : 'pointer',
              opacity: creating ? 0.7 : 1
            }}
          >
            {creating ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Archive size={16} />}
            {creating ? 'Creating...' : 'Create Backup Now'}
          </button>
        </div>

        {backups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: C.textSecondary }}>
            <Database size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
            <p>No backups found. Create your first backup above.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {backups.slice(0, 10).map(b => (
              <div key={b.backup_id} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: C.bgPrimary, borderRadius: 8, gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>{b.backup_id}</div>
                  <div style={{ fontSize: 12, color: C.textSecondary, marginTop: 2 }}>
                    {new Date(b.created_at).toLocaleString()} • {(b.size_bytes / 1024).toFixed(1)} KB
                    {b.source === 'r2' && <span style={{ marginLeft: 8, color: theme.statusSuccess }}>☁️ Cloud</span>}
                    {b.local_cached && <span style={{ marginLeft: 8, color: C.textSecondary }}>💾 Cached</span>}
                  </div>
                </div>
                <button 
                  onClick={() => downloadBackup(b.backup_id)}
                  style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: C.textPrimary }}
                >
                  <Download size={14} /> Download
                </button>
                <button 
                  data-testid={`restore-btn-${b.backup_id}`}
                  onClick={() => openRestoreModal(b)}
                  style={{ background: theme.statusError, color: theme.textInverse, border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}
                >
                  <RefreshCw size={14} /> Restore
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Export Section */}
      <div style={{ background: C.bgCard, borderRadius: 12, padding: 20, marginBottom: 20, border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <FileText size={20} color={C.textPrimary} />
          <h3 style={{ fontSize: 16, fontWeight: 600, color: C.textPrimary, margin: 0 }}>Export Data (CSV)</h3>
        </div>
        
        <p style={{ fontSize: 13, color: C.textSecondary, marginBottom: 16 }}>
          Download CSV exports for offline access. Each export includes timestamp, environment label, and record count.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <ExportButton label="Guests" type="guests" exporting={exporting} onClick={() => exportData('guests')} />
          <ExportButton label="Email Logs" type="email-logs" exporting={exporting} onClick={() => exportData('email-logs')} />
          <ExportButton label="SMS Logs" type="sms-logs" exporting={exporting} onClick={() => exportData('sms-logs')} />
          <ExportButton label="Interactions" type="interactions" exporting={exporting} onClick={() => exportData('interactions')} />
          <button
            data-testid="export-all-btn"
            onClick={() => exportData('all')}
            disabled={exporting === 'all'}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: C.gold, color: theme.textPrimary, border: 'none', borderRadius: 8,
              padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: exporting === 'all' ? 'wait' : 'pointer',
              opacity: exporting === 'all' ? 0.7 : 1
            }}
          >
            {exporting === 'all' ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={16} />}
            Export All (ZIP)
          </button>
        </div>
      </div>

      {/* Deletion Policy Section */}
      <div style={{ background: C.bgCard, borderRadius: 12, padding: 20, border: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <Shield size={20} color={C.textPrimary} />
          <h3 style={{ fontSize: 16, fontWeight: 600, color: C.textPrimary, margin: 0 }}>Deletion Policy</h3>
        </div>

        <div style={{ display: 'grid', gap: 12 }}>
          <PolicyRow 
            entity="Guests" 
            type="Soft Delete" 
            retention="Indefinite (until trash emptied)"
            restorable={true}
            method="Trash Bin → Restore"
          />
          <PolicyRow 
            entity="Guests (Trash)" 
            type="Hard Delete" 
            retention="Permanent"
            restorable={false}
            method="Backup restore only"
          />
          <PolicyRow 
            entity="Email Logs" 
            type="Hard Delete" 
            retention="Permanent"
            restorable={false}
            method="Backup restore only"
            note="Audit trail preserved in Interactions"
          />
          <PolicyRow 
            entity="SMS Logs" 
            type="Hard Delete" 
            retention="Permanent"
            restorable={false}
            method="Backup restore only"
            note="Audit trail preserved in Interactions"
          />
          <PolicyRow 
            entity="Interactions (Audit)" 
            type="Immutable" 
            retention="Forever"
            restorable="N/A"
            method="Cannot be deleted"
            highlight
          />
        </div>
      </div>

      {/* Restore Modal */}
      {restoreModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.bgCard, borderRadius: 14, padding: 24, maxWidth: 600, width: '90%', maxHeight: '80vh', overflow: 'auto', border: `1px solid ${C.border}` }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: C.textPrimary }}>
              <RefreshCw size={20} /> Restore from Backup
            </h3>

            <div style={{ background: theme.statusWarningTint, borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: theme.statusWarning }}>
              <strong>⚠️ Warning:</strong> Restoring will overwrite current data. A pre-restore snapshot will be created automatically.
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: C.textPrimary }}>Backup Details</div>
              <div style={{ background: C.bgPrimary, borderRadius: 8, padding: 12, fontSize: 13, color: C.textSecondary }}>
                <div><strong style={{ color: C.textPrimary }}>ID:</strong> {restoreModal.backup_id}</div>
                <div><strong style={{ color: C.textPrimary }}>Created:</strong> {new Date(restoreModal.created_at).toLocaleString()}</div>
                <div><strong style={{ color: C.textPrimary }}>Environment:</strong> {restoreModal.validation?.environment || 'unknown'}</div>
                <div><strong style={{ color: C.textPrimary }}>Total Records:</strong> {restoreModal.validation?.total_records || 0}</div>
              </div>
            </div>

            {restoreModal.validation?.collections && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8, color: C.textPrimary }}>What will be restored</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                  {Object.entries(restoreModal.validation.collections).map(([name, info]) => (
                    <div key={name} style={{ background: C.bgPrimary, borderRadius: 6, padding: '8px 12px', fontSize: 13, color: C.textSecondary }}>
                      <strong style={{ color: C.textPrimary }}>{name}:</strong> {info.record_count} records
                    </div>
                  ))}
                </div>
              </div>
            )}

            {restoreModal.validation?.warnings?.length > 0 && (
              <div style={{ background: theme.statusErrorTint, borderRadius: 8, padding: 12, marginBottom: 16 }}>
                {restoreModal.validation.warnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 13, color: theme.statusError }}>⚠️ {w}</div>
                ))}
              </div>
            )}

            {restoreState.result && (
              <div style={{ 
                background: restoreState.result.status === 'completed' ? theme.statusSuccessTint : restoreState.result.status === 'dry_run_complete' ? 'rgba(59,130,246,0.15)' : theme.statusErrorTint,
                borderRadius: 8, padding: 12, marginBottom: 16 
              }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: restoreState.result.status === 'completed' ? theme.statusSuccess : restoreState.result.status === 'dry_run_complete' ? '#60a5fa' : theme.statusError }}>
                  {restoreState.result.status === 'completed' && '✅ Restore completed successfully!'}
                  {restoreState.result.status === 'dry_run_complete' && '✅ Dry run validation passed'}
                  {restoreState.result.status === 'failed' && `❌ ${restoreState.result.error}`}
                </div>
                {restoreState.result.pre_restore_snapshot_id && (
                  <div style={{ fontSize: 12, color: C.textSecondary }}>Pre-restore snapshot: {restoreState.result.pre_restore_snapshot_id}</div>
                )}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 14, fontWeight: 500, display: 'block', marginBottom: 8, color: C.textPrimary }}>
                Type "RESTORE" to confirm
              </label>
              <input
                data-testid="restore-confirmation-input"
                type="text"
                value={restoreState.confirmation}
                onChange={e => setRestoreState(s => ({ ...s, confirmation: e.target.value }))}
                placeholder="Type RESTORE"
                style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box', background: C.bgInput, color: C.textPrimary }}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRestoreModal(null)}
                style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.bgPrimary, cursor: 'pointer', fontSize: 14, color: C.textPrimary }}
              >
                Cancel
              </button>
              <button
                data-testid="dry-run-btn"
                onClick={() => runRestore(true)}
                disabled={restoreState.running}
                style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: '#3B82F6', color: '#fff', cursor: 'pointer', fontSize: 14, opacity: restoreState.running ? 0.7 : 1 }}
              >
                {restoreState.running ? 'Running...' : 'Dry Run (Validate)'}
              </button>
              <button
                data-testid="confirm-restore-btn"
                onClick={() => runRestore(false)}
                disabled={restoreState.running || restoreState.confirmation !== 'RESTORE'}
                style={{ 
                  padding: '10px 20px', borderRadius: 8, border: 'none', 
                  background: restoreState.confirmation === 'RESTORE' ? theme.statusError : C.bgPrimary,
                  color: restoreState.confirmation === 'RESTORE' ? theme.textInverse : C.textSecondary,
                  cursor: restoreState.confirmation === 'RESTORE' ? 'pointer' : 'not-allowed',
                  fontSize: 14, opacity: restoreState.running ? 0.7 : 1
                }}
              >
                {restoreState.running ? 'Restoring...' : 'Restore Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const StatusItem = ({ label, status, ok, notOk, value }) => (
  <div style={{ background: C.bgPrimary, borderRadius: 8, padding: 12 }}>
    <div style={{ fontSize: 12, color: C.textSecondary, marginBottom: 4 }}>{label}</div>
    {value !== undefined ? (
      <div style={{ fontSize: 14, fontWeight: 500, color: C.textPrimary }}>{value}</div>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {status ? (
          <>
            <CheckCircle size={16} color={theme.statusSuccess} />
            <span style={{ fontSize: 14, fontWeight: 500, color: theme.statusSuccess }}>{ok}</span>
          </>
        ) : (
          <>
            <AlertTriangle size={16} color={theme.statusError} />
            <span style={{ fontSize: 14, fontWeight: 500, color: theme.statusError }}>{notOk}</span>
          </>
        )}
      </div>
    )}
  </div>
);

const ExportButton = ({ label, type, exporting, onClick }) => (
  <button
    data-testid={`export-${type}-btn`}
    onClick={onClick}
    disabled={exporting === type}
    style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: C.bgPrimary, color: C.textPrimary, border: `1px solid ${C.border}`, borderRadius: 8,
      padding: '10px 16px', fontSize: 14, cursor: exporting === type ? 'wait' : 'pointer',
      opacity: exporting === type ? 0.7 : 1
    }}
  >
    {exporting === type ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={14} />}
    {label}
  </button>
);

const PolicyRow = ({ entity, type, retention, restorable, method, note, highlight }) => (
  <div style={{ 
    display: 'grid', 
    gridTemplateColumns: '140px 100px 180px 80px 1fr', 
    gap: 12, 
    padding: '10px 12px', 
    background: highlight ? theme.statusWarningTint : C.bgPrimary, 
    borderRadius: 8, 
    fontSize: 13,
    alignItems: 'center'
  }}>
    <div style={{ fontWeight: 500, color: C.textPrimary }}>{entity}</div>
    <div style={{ color: type === 'Immutable' ? theme.statusSuccess : type === 'Hard Delete' ? theme.statusError : theme.statusWarning }}>{type}</div>
    <div style={{ color: C.textSecondary }}>{retention}</div>
    <div>
      {restorable === true && <span style={{ color: theme.statusSuccess }}>✓ Yes</span>}
      {restorable === false && <span style={{ color: theme.statusError }}>✗ No</span>}
      {restorable === 'N/A' && <span style={{ color: C.textSecondary }}>N/A</span>}
    </div>
    <div style={{ color: C.textSecondary, fontSize: 12 }}>
      {method}
      {note && <div style={{ color: C.textMuted, marginTop: 2 }}>{note}</div>}
    </div>
  </div>
);

export default DataProtectionTab;
